/**
 * Librería de centralización de sockets y cotizaciones.
 * Contiene los modelos de suscripción a mercados, índices, divisas, etc.
 * Ofrece además una serie de utilizades JS que son de utilidad a la hora de manipular los datos de cotizaciones.
 *
 * Los módulos del Web pueden utilizar cualquiera de las funciones públicas del objeto eiLiveSockets para suscribirse
 * a cambios lanzados por el socket de Qbitia. Esta librería centralizará todas las suscripciones evitando repetir conexiones
 * para cada módulo. De esta forma, aprovecha conexiones abiertas siempre y cuando la suscripción sea por mercado. *
 * En streaming directo (como una serie de tickerIds), no puede evitar que haya varias conexiones con el mismo idTicker en cada
 * una de ellas, por lo que se debe utilizar con cierta cautela.
 *
 * El callback que se ejecuta por defecto modificará el valor de la cotización acorde al enviado por Qbitia y ajustado a los decimales
 * por defecto según recoge el módulo utils. Si se necesitase ejecutar una función especializada, podrá adjuntar un cb con la firma tal que
 * function(json){}. Tenga en cuenta que es posible que se ejecute tanto el callback por defecto, por lo que deberá controlar estos cambios
 * en la función adjunta (evitando colisiones).
 *
 *
 * @version 0.0.5 Librería pasada a ES2016 y varios bugs de conexión solucionados
 * version 0.0.4 Librería con Callback por defecto y a medida, con funciones de interés incluidas
 *                Incluye la llamada al Socket pasando diferentes dataTypes separados por comas.
 *                Cambia el sistema de comprobación de doble socket abierto
 */

import {customCotFormat, number_format, toFixedNumber, getColorFormat, getDomCss} from './utils';
import {subscribeByIndex, subscribeByMarket, subscribeByTickerIdSeries, socketsDebug} from './suscriptions';
import './streaming.scss';

const VERSION = '0.0.5';

const returnFunctions = {
    /**
     * Devuelve la versión de esta librería
     * @returns {string}
     */
    version: function(){
        return VERSION;
    },

    /**
     * Se suscribe a LAS ACCIONES de un mercado, sin tener en cuenta los índices.
     * Si se proporciona cb, se ejecutará; si no, se ejecutará el CB por defecto.
     *
     * @param {string} marketId Identificador del mercado
     * @param {null|function} cb cb a ejecutar cuando llegue un mensaje o null para callback por defecto
     */
    subscribeStockByMarket: (marketId, cb) => subscribeByMarket(marketId, 0, cb),

    /**
     * Se suscribe a LAS DIVISAS de un mercado, sin tener en cuenta los índices.
     * Si se proporciona cb, se ejecutará; si no, se ejecutará el CB por defecto.
     * @param {string} marketId Identificador del mercado
     * @param {function|null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeForexByMarket: (marketId, cb) => subscribeByMarket(marketId, 2, cb),

    /**
     * Se suscribe a LAS CRIPTODIVISAS de un mercado, sin tener en cuenta los índices.
     * Si se proporciona cb, se ejecutará; si no, se ejecutará el CB por defecto.
     * @param {string} marketId Identificador del mercado
     * @param {function|null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeCriptoByMarket: (marketId, cb) => subscribeByMarket(marketId, 14, cb),

    /**
     * Se suscribe a LAS MATERIAS PRIMAS de un mercado, sin tener en cuenta los índices.
     * Si se proporciona cb, se ejecutará; si no, se ejecutará el CB por defecto.
     * @param {string} marketId Identificador del mercado
     * @param {function|null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeCommodityByMarket: (marketId, cb) => subscribeByMarket(marketId, 12, cb),

    /**
     * Se suscribe a las ACCIONES e ÍNDICE de un mercado identificado por un marketId.
     * Si se proporciona cb, se ejecutará; si no, se ejecutará el CB por defecto.
     * @param {string, number} marketId Identificador del mercado
     * @param {function|null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeStockIndexByMarket: (marketId, cb = null) => subscribeByMarket(marketId, [0,4], cb),

    /**
     * Se suscribe a las ACCIONES e ÍNDICES de un mercado identificando dicho mercado a través
     * del indexID de un índice; es decir, a través del identificador de un índice, determina un mercado
     * y se suscribe a los COMPONENTES e ÍNDICES de dicho mercado.
     * @param {string|number} indexId Identificador de un índice
     * @param {function|null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeStockIndexByIndex: (indexId, cb) => subscribeByIndex(indexId, cb),

    /**
     * @deprecated Para una semántica autoexplicativa, use subscribeStockIndexByIndex en su lugar
     * Atajo subscribeStockIndexByIndex
     * @param {string} indexId Identificador de un índice
     * @param {function|null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeStockByIndex: (indexId, cb) => subscribeByIndex(indexId, cb),

    /**
     * Se suscribe a los tickerIDs
     * Las divisas deben conectarse por esta vía
     * @param {string|array} ids String con tickerId o array de tickerId a los que se desea suscribir
     * @param {function,null} cb a ejecutar cuando llegue un mensaje o null para callback por defecto.
     */
    subscribeTickerIDS: (ids, cb = null) => subscribeByTickerIdSeries(ids, cb),

    /**
     * Devuelve información interna de la librería, tal como el estado de los "hashmap" de conexiones
     * Solo disponible con webpack configurado en desarrollo.
     */
    debug: ()=> {
        if(_WPCONST_ENV_ === 'DEVELOPMENT'){
            socketsDebug()
        }else{
            console.error('Not available');
        }
    },

    /**
     * Función que funciona como la homónima de PHP
     * @param {string} number Número a formatear
     * @param {number} decimals Número de decimales a mostrar
     * @param {string} dec_point Dígito de separación decimal
     * @param {string} thousands_sep Dígito de separación de millares
     * @returns {string}
     */
    number_format: number_format,

    /**
     * Función que, utilizando number_format, define el formateo de una cantidad a través del tipo de campo.
     * @param {string} type Tipo de campo: last, vol, varperc, etc.
     * @param {number, string} value Valor a formatear
     * @param {number, string} Número de decimales
     * @returns {string}
     */
    customCotFormat:customCotFormat,

    /**
     * Función que redondea un número, de forma igual que toFixed, pero devuelve el número
     * en formato numérico.
     * @param {number} number Número a redondear
     * @param {number} decimals Número de decimales
     * @param {number} base Base de cálculo. Por defecto base 10 (decimal).
     */
    toFixedNumber:toFixedNumber,

    /**
     * Función que pinta el estilo para el dato value en funcion de si es positivo, negativo o 0
     * en formato numérico.
     * @param {number} value Valor utilizado para formatear el dato
     */
    getColorFormat:getColorFormat,

    /**
     * Función que obtiene el objeto y setea y elimina del doom el estilo adecuado al dato numerico
     * en formato numérico.
     * @param {number} value Valor utilizado para formatear el dato
     * @param {object} domElement Elemento del Dom a tratar
     */
    getDomCss: getDomCss
};

export default returnFunctions;